import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const REASON_COLORS = ["#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6", "#6b7280"];

// ============================================================================
// OPERATOR COACHING PROFILE (accessed from Operators page)
// ============================================================================

export const OperatorCoachingProfile = ({ operatorId, operatorName }) => {
  const [quality, setQuality] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filterType, setFilterType] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        const [qRes, dRes] = await Promise.all([
          axios.get(`${API}/decisions/quality-summary?operator_id=${operatorId}`),
          axios.get(`${API}/decisions?operator_id=${operatorId}&limit=100`),
        ]);
        setQuality(qRes.data);
        setDecisions(dRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [operatorId]);

  if (loading) return <p className="text-slate-500 text-sm p-4">Loading coaching profile...</p>;

  const op = quality?.operators?.[0];
  const reasons = quality?.top_override_reasons || [];

  const pieData = reasons.map((r, i) => ({
    name: r.reason?.length > 25 ? r.reason.substring(0, 25) + "..." : r.reason,
    value: r.count,
    fill: REASON_COLORS[i % REASON_COLORS.length],
  }));

  const decisionBreakdown = [
    { name: "Followed", value: op?.followed || 0, fill: "#F59E0B" },
    { name: "Overridden", value: op?.overridden || 0, fill: "#f59e0b" },
    { name: "Ignored", value: op?.ignored || 0, fill: "#6b7280" },
  ];

  const filteredDecisions = decisions
    .filter(d => !filterType || d.decision_type === filterType)
    .sort((a, b) => {
      const av = a[sortCol] || "";
      const bv = b[sortCol] || "";
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  return (
    <div className="space-y-6" data-testid="operator-coaching-profile">
      <h3 className="text-lg font-semibold text-white">Coaching Profile: {operatorName || operatorId}</h3>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-3">
        <SummaryCard label="Total Decisions" value={op?.total_decisions || 0} />
        <SummaryCard label="Follow Rate" value={op?.follow_rate_pct != null ? `${op.follow_rate_pct}%` : "N/A"} color="text-emerald-400" />
        <SummaryCard label="Override Rate" value={op?.total_decisions ? `${((op.overridden / op.total_decisions) * 100).toFixed(1)}%` : "N/A"} color="text-amber-400" />
        <SummaryCard label="Outcome Accuracy" value={op?.outcome_accuracy_pct != null ? `${op.outcome_accuracy_pct}%` : "N/A"} color="text-blue-400" />
        <SummaryCard label="Correct/Incorrect" value={`${op?.outcomes_correct || 0}/${op?.outcomes_incorrect || 0}`} />
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Decision Breakdown */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Decision Breakdown</h4>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={decisionBreakdown} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3}>
                {decisionBreakdown.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#252218", border: "1px solid #352F24", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Override Reasons */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Override Reasons</h4>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={pieData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#352F24" />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#9E9880" }} />
                <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 9, fill: "#9E9880" }} />
                <Tooltip contentStyle={{ background: "#252218", border: "1px solid #352F24", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" name="Count" radius={[0, 4, 4, 0]}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-500 text-xs">No overrides recorded</p>}
        </div>
      </div>

      {/* Decision History Table */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white">Decision History</h4>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            data-testid="decision-type-filter"
            className="bg-slate-800 text-white text-xs rounded px-2 py-1 border border-slate-700">
            <option value="">All Types</option>
            {["SCALE", "KILL", "CREATIVE_APPROVAL", "ASSIGNMENT_REQUEST", "ANGLE_SELECTION", "OVERRIDE"].map(t =>
              <option key={t} value={t}>{t}</option>
            )}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="decision-history-table">
            <thead>
              <tr className="text-slate-500 border-b border-slate-800">
                {[
                  { key: "created_at", label: "Date" },
                  { key: "decision_type", label: "Type" },
                  { key: "system_recommendation", label: "System Recommendation" },
                  { key: "operator_decision", label: "Decision" },
                  { key: "reasoning", label: "Reasoning" },
                  { key: "outcome", label: "Outcome" },
                ].map(col => (
                  <th key={col.key} onClick={() => toggleSort(col.key)}
                    className="text-left py-2 px-2 cursor-pointer hover:text-white">
                    {col.label} {sortCol === col.key ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredDecisions.map(d => (
                <tr key={d.decision_id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="py-2 px-2 text-slate-400">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
                  <td className="py-2 px-2 text-white">{d.decision_type}</td>
                  <td className="py-2 px-2 text-slate-300 max-w-[200px] truncate">{d.system_recommendation || "—"}</td>
                  <td className="py-2 px-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      d.operator_decision === "FOLLOWED" ? "bg-emerald-500/20 text-emerald-400" :
                      d.operator_decision === "OVERRIDDEN" ? "bg-amber-500/20 text-amber-400" : "bg-slate-600/50 text-slate-400"
                    }`}>{d.operator_decision}</span>
                  </td>
                  <td className="py-2 px-2 text-slate-400 max-w-[150px] truncate">{d.reasoning || "—"}</td>
                  <td className="py-2 px-2">
                    {d.outcome ? (
                      <span className={`text-xs font-medium ${
                        d.outcome === "CORRECT" ? "text-emerald-400" : d.outcome === "INCORRECT" ? "text-red-400" : "text-slate-400"
                      }`}>{d.outcome}</span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              ))}
              {filteredDecisions.length === 0 && (
                <tr><td colSpan={6} className="text-center py-4 text-slate-500">No decisions found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// OPERATOR COMPARISON VIEW (for Founder Dashboard)
// ============================================================================

export const OperatorComparisonWidget = () => {
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/decisions/quality-summary`).then(r => setQuality(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  const operators = quality?.operators || [];
  if (operators.length === 0) return null;

  const chartData = operators.map(op => ({
    name: op.operator_id?.substring(0, 12) || "?",
    follow_rate: op.follow_rate_pct || 0,
    accuracy: op.outcome_accuracy_pct || 0,
    decisions: op.total_decisions,
  }));

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid="operator-comparison-widget">
      <h3 className="text-sm font-semibold text-white mb-3">Operator Decision Quality</h3>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#352F24" />
          <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#9E9880" }} />
          <YAxis tick={{ fontSize: 10, fill: "#9E9880" }} domain={[0, 100]} />
          <Tooltip contentStyle={{ background: "#252218", border: "1px solid #352F24", borderRadius: 8, fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <Bar dataKey="follow_rate" name="Follow Rate %" fill="#F59E0B" radius={[4, 4, 0, 0]} />
          <Bar dataKey="accuracy" name="Outcome Accuracy %" fill="#3b82f6" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-slate-500 border-b border-slate-800">
              <th className="text-left py-1 px-2">Operator</th>
              <th className="text-right py-1 px-2">Decisions</th>
              <th className="text-right py-1 px-2">Follow %</th>
              <th className="text-right py-1 px-2">Override %</th>
              <th className="text-right py-1 px-2">Accuracy %</th>
            </tr>
          </thead>
          <tbody>
            {operators.map(op => (
              <tr key={op.operator_id} className="border-b border-slate-800/50">
                <td className="py-1 px-2 text-white">{op.operator_id?.substring(0, 16)}</td>
                <td className="py-1 px-2 text-right text-slate-400">{op.total_decisions}</td>
                <td className="py-1 px-2 text-right text-emerald-400">{op.follow_rate_pct}%</td>
                <td className="py-1 px-2 text-right text-amber-400">
                  {op.total_decisions ? ((op.overridden / op.total_decisions) * 100).toFixed(1) : 0}%
                </td>
                <td className="py-1 px-2 text-right text-blue-400">{op.outcome_accuracy_pct ?? "—"}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, color = "text-white" }) => (
  <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-3">
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <p className={`text-lg font-bold ${color}`}>{value}</p>
  </div>
);
